import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  MessageCircle, MessageSquare, Search, X, Check, RefreshCw, ArrowLeft, Quote,
} from 'lucide-react';
import { getQuotes } from '../../services/api';

/* Person-to-person deep links. The app composes the message; the client sends
   it from their own WhatsApp or Messages app. No A2P registration applies. */
function waLink(phone, text) {
  const digits = String(phone || '').replace(/\D/g, '');   // wa.me wants digits only
  return `https://wa.me/${digits}?text=${encodeURIComponent(text || '')}`;
}

function smsLink(phone, text) {
  // "?&body=" is the form both iOS Messages and Android accept
  return `sms:${phone}?&body=${encodeURIComponent(text || '')}`;
}

const firstName = (full) => String(full || '').trim().split(/\s+/)[0];

/** The exact text that will land on the athlete's phone. */
function composeMessage(quote, athlete) {
  if (!quote) return '';
  const who = athlete ? `${firstName(athlete.name)} — ` : '';
  return `${who}"${quote.text}"\n— ${quote.author}`;
}

/* Who has already been sent something today, so a long roster doesn't mean
   remembering where you got to. Per-device, resets daily. */
const DONE_KEY = 'rdi.module02.sent';
const today = () => new Date().toISOString().slice(0, 10);

function loadDone() {
  try {
    const raw = JSON.parse(localStorage.getItem(DONE_KEY) || '{}');
    return raw.date === today() ? raw.ids || {} : {};
  } catch (_) { return {}; }
}

export default function Module02({ data }) {
  const { athletes = [] } = data;

  const [quote, setQuote] = useState(null);   // chosen quotation
  const [seen, setSeen] = useState([]);       // ids already offered
  const [done, setDone] = useState(loadDone);

  useEffect(() => {
    try { localStorage.setItem(DONE_KEY, JSON.stringify({ date: today(), ids: done })); }
    catch (_) { /* private mode — feature degrades, page still works */ }
  }, [done]);

  const markSent = (id) => setDone((d) => ({ ...d, [id]: true }));

  return quote
    ? <PickAthlete
        quote={quote}
        athletes={athletes}
        done={done}
        onBack={() => setQuote(null)}
        onSent={markSent}
      />
    : <PickQuote
        onPick={setQuote}
        seen={seen}
        onSeen={setSeen}
        sentCount={Object.keys(done).length}
        total={athletes.length}
      />;
}

/* ── Step 1 — choose the quotation ───────────────────────────────── */

function PickQuote({ onPick, seen, onSeen, sentCount, total }) {
  const [tag, setTag] = useState(null);
  const [batch, setBatch] = useState(0);   // bump to force a new batch

  const { data, isFetching, error, refetch } = useQuery({
    queryKey: ['quotes', tag, batch],
    queryFn: () => getQuotes({ count: 6, seen, tag }),
    staleTime: 0,
  });

  // Remember what's been offered so "More" keeps moving through the pool.
  useEffect(() => {
    if (data?.quotes?.length) {
      onSeen((prev) => [...new Set([...prev, ...data.quotes.map((q) => q.id)])]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const more = () => { setBatch((b) => b + 1); refetch(); };

  return (
    <div className="space-y-5">
      <div>
        <p className="text-[9px] tracking-[0.3em] uppercase text-gray-600">Step 1 of 2</p>
        <h2 className="text-lg font-light tracking-[0.08em] uppercase mt-1">
          Choose a <span className="font-semibold">quotation</span>
        </h2>
        {total > 0 && (
          <p className="text-[9px] tracking-[0.18em] uppercase text-gray-600 mt-1">
            {sentCount}/{total} athletes messaged today
          </p>
        )}
      </div>

      {/* Theme filter */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
        <Chip active={!tag} onClick={() => setTag(null)}>All</Chip>
        {(data?.tags || []).map((t) => (
          <Chip key={t} active={tag === t} onClick={() => setTag(t)}>{t}</Chip>
        ))}
      </div>

      {error && (
        <p className="text-[10px] tracking-[0.2em] uppercase text-center text-red-400/80 py-8">
          Could not load quotations
        </p>
      )}

      {isFetching && !data && (
        <p className="text-[10px] tracking-[0.2em] uppercase text-gray-600 text-center py-10">
          Loading…
        </p>
      )}

      <div className="space-y-3">
        {(data?.quotes || []).map((q) => (
          <button
            key={q.id}
            onClick={() => onPick(q)}
            className="w-full text-left border border-white/10 hover:border-white/25 active:opacity-70
              transition-all p-4 space-y-2"
          >
            <Quote size={13} style={{ color: 'var(--gold-dim)' }} />
            <p className="text-sm text-gray-200 leading-relaxed">{q.text}</p>
            <div className="flex items-center justify-between pt-1">
              <span className="text-[9px] tracking-[0.2em] uppercase text-gray-500">— {q.author}</span>
              <span className="text-[8px] tracking-[0.2em] uppercase text-gray-700">{q.tag}</span>
            </div>
          </button>
        ))}
      </div>

      <button
        onClick={more}
        disabled={isFetching}
        className="w-full flex items-center justify-center gap-2 py-3.5 border transition-all
          disabled:opacity-40 active:opacity-70"
        style={{ borderColor: 'var(--gold)', color: 'var(--gold)' }}
      >
        <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
        <span className="text-[10px] tracking-[0.25em] uppercase font-medium">
          {isFetching ? 'Finding…' : 'More quotations'}
        </span>
      </button>
    </div>
  );
}

/* ── Step 2 — choose one athlete, then send ──────────────────────── */

function PickAthlete({ quote, athletes, done, onBack, onSent }) {
  const [query, setQuery] = useState('');
  const [sport, setSport] = useState('all');
  const [selectedId, setSelectedId] = useState(null);   // exactly one, or none

  const counts = useMemo(() => ({
    all: athletes.length,
    Basketball: athletes.filter((a) => a.sport === 'Basketball').length,
    Football: athletes.filter((a) => a.sport === 'Football').length,
  }), [athletes]);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return athletes
      .filter((a) => sport === 'all' || a.sport === sport)
      .filter((a) => !q
        || a.name.toLowerCase().includes(q)
        || (a.team || '').toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [athletes, query, sport]);

  const selected = athletes.find((a) => a.id === selectedId) || null;
  const message = composeMessage(quote, selected);

  return (
    <div className="space-y-4 pb-36">

      <button onClick={onBack}
        className="flex items-center gap-2 text-[9px] tracking-[0.25em] uppercase text-gray-500 active:opacity-60">
        <ArrowLeft size={13} /> Change quotation
      </button>

      {/* The chosen quote stays visible */}
      <div className="border-l-2 pl-4 py-1" style={{ borderColor: 'var(--gold)' }}>
        <p className="text-sm text-gray-200 leading-relaxed">{quote.text}</p>
        <p className="text-[9px] tracking-[0.2em] uppercase text-gray-500 mt-1.5">— {quote.author}</p>
      </div>

      <div>
        <p className="text-[9px] tracking-[0.3em] uppercase text-gray-600">Step 2 of 2</p>
        <h2 className="text-lg font-light tracking-[0.08em] uppercase mt-1">
          Select <span className="font-semibold">one athlete</span>
        </h2>
      </div>

      {/* Search + filter */}
      <div className="sticky top-0 z-20 bg-black pt-1 pb-3 -mx-6 px-6 space-y-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name or team"
            autoComplete="off"
            className="w-full bg-white/5 border border-white/10 focus:border-white/30 outline-none
              pl-9 pr-9 py-3 text-sm text-white placeholder:text-gray-600 transition-colors"
          />
          {query && (
            <button onClick={() => setQuery('')} aria-label="Clear"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-500 active:opacity-60">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {['all', 'Basketball', 'Football'].map((k) => (
            <Chip key={k} active={sport === k} onClick={() => setSport(k)}>
              {k === 'all' ? 'All' : k} {counts[k]}
            </Chip>
          ))}
        </div>
      </div>

      {list.length === 0 && (
        <p className="text-[10px] tracking-[0.2em] uppercase text-gray-600 text-center py-10">
          No one matches &ldquo;{query}&rdquo;
        </p>
      )}

      {/* Single-select: picking one clears the other */}
      <div className="border-t border-white/8">
        {list.map((a) => {
          const isSel = a.id === selectedId;
          return (
            <button
              key={a.id}
              onClick={() => setSelectedId(isSel ? null : a.id)}
              className="w-full flex items-center gap-3 py-3.5 border-b border-white/8 text-left
                active:opacity-70 transition-opacity"
            >
              <span
                className="w-4 h-4 shrink-0 rounded-full border flex items-center justify-center"
                style={isSel
                  ? { borderColor: 'var(--gold)', background: 'var(--gold)' }
                  : { borderColor: 'rgba(255,255,255,0.22)' }}
              >
                {isSel && <Check size={10} className="text-black" strokeWidth={3} />}
              </span>
              <span className="flex-1 min-w-0">
                <span className={`block text-sm font-medium tracking-wide truncate
                  ${done[a.id] ? 'text-gray-500' : 'text-white'}`}>
                  {a.name}
                </span>
                <span className="block text-[9px] tracking-[0.18em] uppercase text-gray-600 mt-0.5 truncate">
                  {[a.sport, a.team].filter(Boolean).join(' · ')}
                  {done[a.id] ? ' · sent today' : ''}
                </span>
              </span>
              {!a.phone && (
                <span className="text-[8px] tracking-[0.15em] uppercase text-gray-700 shrink-0">no number</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Send bar — only once exactly one athlete is chosen */}
      {selected && (
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-black border-t border-white/10">
          <div className="max-w-lg mx-auto px-6 py-4 space-y-3">
            <p className="text-[9px] tracking-[0.18em] uppercase text-gray-500">
              To {selected.name}{selected.phone ? ` · ${selected.phone}` : ''}
            </p>
            <p className="text-[11px] text-gray-400 leading-snug whitespace-pre-line
              border border-white/8 bg-white/3 px-3 py-2 max-h-24 overflow-y-auto">
              {message}
            </p>
            {selected.phone ? (
              <div className="flex gap-2">
                <a
                  href={waLink(selected.phone, message)}
                  target="_blank" rel="noopener noreferrer"
                  onClick={() => onSent(selected.id)}
                  className="flex-1 flex items-center justify-center gap-2 py-3 border border-white/15
                    text-gray-200 active:opacity-70"
                >
                  <MessageCircle size={14} />
                  <span className="text-[10px] tracking-[0.2em] uppercase font-medium">WhatsApp</span>
                </a>
                <a
                  href={smsLink(selected.phone, message)}
                  onClick={() => onSent(selected.id)}
                  className="flex-1 flex items-center justify-center gap-2 py-3 border active:opacity-70"
                  style={{ borderColor: 'var(--gold)', color: 'var(--gold)' }}
                >
                  <MessageSquare size={14} />
                  <span className="text-[10px] tracking-[0.2em] uppercase font-medium">Messages</span>
                </a>
              </div>
            ) : (
              <p className="text-[9px] tracking-[0.15em] uppercase text-gray-600 text-center py-2">
                No phone number on file for {selected.name}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Chip({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className="flex-shrink-0 px-3 py-1.5 text-[9px] tracking-[0.2em] uppercase border transition-all
        active:opacity-70"
      style={active
        ? { borderColor: 'var(--gold)', color: 'var(--gold)' }
        : { borderColor: 'rgba(255,255,255,0.1)', color: '#5a5a5a' }}
    >
      {children}
    </button>
  );
}
